import React, { useState, useEffect, useRef } from "react";
import Header from "./Header";
import { Link, useLocation } from "react-router-dom";
import Footer from "./Footer";
import {
  blogsSearch,
  getBlogCategories,
  getBlogs,
} from "../../redux/actions/API/blogs";
import { connect } from "react-redux";
import "./Blogs.css";
import Loader from "../Loader/Loader";
import {
  Col,
  Input,
  notification,
  Row,
  Pagination,
  Select,
  Skeleton,
  Space,
} from "antd";
import { IDataResponse } from "../../redux/types/API/ApiResponse";
import noRecord from "../../Utility/images/norecord.png";
import { SearchProps } from "antd/es/input";
import { ArrowRightOutlined } from "@ant-design/icons";
import { scrollToElementById } from "../../Utility/scrollUtils";
import { Helmet } from "react-helmet-async";
interface IBlogs {
  blogsData: any[];
  blogCategoriesData: any;
  filteredData: any[];
  getBlogs: Function;
  getBlogsState: IDataResponse;
  blogsSearch: Function;
  blogSearchState: IDataResponse;
  getBlogCategories: Function;
  getBlogCategoriesState: IDataResponse;
}
type NotificationType = "success" | "info" | "warning" | "error";

const { Search } = Input;

const openNotificationWithIcon = (type: NotificationType, message: string) => {
  notification.open({
    message: message,
    type: type,
    duration: 3,
  });
};

const Blogs: React.FC<IBlogs> = ({
  getBlogs,
  blogsData,
  filteredData,
  getBlogsState,
  getBlogCategories,
  getBlogCategoriesState,
  blogCategoriesData,
  blogsSearch,
  blogSearchState,
}) => {
  const prevPropsRef = useRef<any>();
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [filteredBlogs, setFilteredBlogs] = useState<any[]>(blogsData);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const [blogCategoryMap, setBlogCategoryMap] = useState<any>({})

  useEffect(() => {
    getBlogs();
    getBlogCategories();
    setFilteredBlogs(blogsData);
  }, []);

  const { pathname } = useLocation();
  useEffect(() => {
    scrollToElementById("blogs-page");
  }, [pathname]);

  useEffect(() => {
    if (
      prevPropsRef?.current?.getBlogsState?.loading &&
      !getBlogsState?.loading
    ) {
      if (getBlogsState?.error) {
        setLoading(false);
        openNotificationWithIcon("error", "Something went wrong!");
      } else {
        setFilteredBlogs(blogsData);
        setLoading(false);
      }
    }
    if (
      prevPropsRef?.current?.getBlogCategoriesState?.loading &&
      !getBlogCategoriesState?.loading
    ) {
      if (getBlogCategoriesState?.error) {
        setLoading(false);
        openNotificationWithIcon("error", "Something went wrong!");
      } else {
        setLoading(false);
        setFilteredBlogs(blogsData);
      }
    }

    if (
      prevPropsRef?.current?.blogSearchState?.loading &&
      !blogSearchState?.loading
    ) {
      if (blogSearchState?.error) {
        setLoading(false);
        setFilteredBlogs(blogsData);
        openNotificationWithIcon("error", "Something went wrong!");
      } else {
        setLoading(false);
        setFilteredBlogs(filteredData);
      }
    }
    prevPropsRef.current = {
      getBlogsState,
      getBlogCategoriesState,
      blogSearchState,
    };
  }, [getBlogsState, getBlogCategoriesState, blogSearchState]);

  useEffect(() => {
    if (Array.isArray(blogCategoriesData)) {
      const obj = blogCategoriesData?.reduce((acc: any, curr: any) => {
        acc[curr.id] = curr.category;
        return acc;
      }, {});
      setBlogCategoryMap(obj);
    }
  }, [blogCategoriesData]);

  const truncateText = (text: any, maxLength = 150) => {
    return text?.length > maxLength ? text?.slice(0, maxLength) + "..." : text;
  };

  const renderBlogs = (blogs: any) => {
    return blogs?.map((blogData: any, index: number) => {
      const updatedAtDate = new Date(blogData.updatedAt).toLocaleDateString();
      return (
        <div id="our-blogs" key={index} className="col-md-6 col-lg-4">
          <div className="blog-item-wrapper">
            <div className="blog-image-wrapper">
              <img
                className="blog-image"
                src={blogData.imageURL}
                alt="blog-post-image"
              />
            </div>
            <div className="blog-post-txt">
              <span className="post-tag">{blogData.header}</span>
              <h6 className="s-20 w-700">
                <Link
                  to={`/blog_details/${blogCategoryMap[blogData?.blogCategory] || 'category'}/${encodeURIComponent(
                    blogData?.URLTitle
                  )
                    .replace(/%20/g, "-")
                    .replace(/%7D/g, "")}`}
                >
                  {blogData.title}
                </Link>
              </h6>
              <p>{truncateText(blogData.shortDesc)}</p>
              <Link
                to={`/blog_details/${blogCategoryMap[blogData?.blogCategory] || 'category'}/${encodeURIComponent(
                  blogData?.URLTitle
                )
                  .replace(/%20/g, "-")
                  .replace(/%7D/g, "")}`}
              >
                <div className="readMore">
                  <span>Read more</span>
                  <ArrowRightOutlined className="readMore-icon" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      );
    });
  };

  const onSearch: SearchProps["onSearch"] = (value) => {
    setLoading(true);
    setSelectedCategory(null);
    setFilteredBlogs(blogsData);
    if (!value) {
      getBlogs();
    } else {
      blogsSearch({ query: value, blogCategory: null });
    }
  };

  const onCategoryChange = (value: string) => {
    setLoading(true);
    setSelectedCategory(value);
    if (!value) {
      getBlogs();
      setFilteredBlogs(blogsData);
    } else {
      blogsSearch({ query: "", blogCategory: value });
    }
  };

  const paginatedBlogs = filteredBlogs?.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const onPageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  return (
    <div id="blogs-page">
      <Helmet>
        <title>Ease margin Blogs | Insights on Trading, Finance & Markets</title>
        <meta
          name="description"
          content="Explore Easemargin blogs for expert insights on trading strategies, financial tips, and market trends to grow your investment knowledge."
        />
        <link
          rel="canonical"
          href="https://easemargin.com/our-blogs"
        />
      </Helmet>

      <Header bgColor="#ffffff" />

      <div className="blogs-page">
        {/* <Loader loading={loading} /> */}
        <div className="blog-header " style={{ textAlign: "center" }}>
          <h2 style={{ fontSize: "32px", fontWeight: 700 }}>
            Insights, updates, and tips from our team to keep you informed.
          </h2>
        </div>
        <section id="blog" className="container">
          <div className="flexBox mb-20">
            <div></div>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Search
                  placeholder="Search blogs..."
                  onSearch={onSearch}
                  enterButton
                  allowClear
                />
              </Col>
              <Col xs={24} md={12}>
                <Select
                  placeholder="Select Category"
                  className="categorySelection"
                  value={selectedCategory}
                  onChange={onCategoryChange}
                  allowClear
                >
                  {blogCategoriesData?.map((item: any) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.category}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </div>

          <div className="row">
            {loading ? (
              <Row gutter={[32, 32]}>
                {Array.from({ length: 6 })?.map((_, i) => (
                  <Col xs={24} sm={12} md={8} key={i}>
                    <div className="simmer-card">
                      <div className="simmer-image"></div>
                      <div className="simmer-lines">
                        <div className="simmer-line long"></div>
                        <div className="simmer-line medium"></div>
                        <div className="simmer-line short"></div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            ) : (paginatedBlogs && paginatedBlogs?.length > 0) ? (
              renderBlogs(paginatedBlogs)
            ) : (
              <div className="noData">
                <img
                  src={noRecord}
                  alt="No Records Found"
                  style={{ width: "auto", height: "300px" }}
                />
              </div>
            )}
          </div>
          <Row justify="center" style={{ marginTop: 40 }}>
            <Col>
              <Space>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={filteredBlogs?.length}
                  onChange={onPageChange}
                  showSizeChanger={false}
                />
              </Space>
            </Col>
          </Row>
        </section>
      </div>
      <Footer />
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  blogsData: state.api.getBlogs?.data,
  filteredData: state.api?.blogSearch?.data,
  getBlogsState: state.api.getBlogs,
  blogCategoriesData: state.api.getBlogCategories?.data,
  getBlogCategoriesState: state.api.getBlogCategories,
  blogSearchState: state.api.blogSearch,
});

const mapDispatchToProps = (dispatch: any) => ({
  getBlogs: (payload: any) => dispatch(getBlogs(payload)),
  getBlogCategories: (payload: any) => dispatch(getBlogCategories(payload)),
  blogsSearch: (payload: any) => dispatch(blogsSearch(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Blogs);
